import { useEffect, useMemo, useRef, useState } from "react";
import BarLoader from "react-spinners/BarLoader";
import { useSelector } from "react-redux";
import { fetchCitiesList } from "./nova-poshta-api";
import styles from "./NovaPoshtaComponent.module.css";
import {
  selectDeliveryAddress,
  selectParcel,
} from "../../redux/form/formSelectors.js";
import icons from "../../image/icons.svg";

const WIDGET_ORIGIN = "https://widget.novapost.com";
const WIDGET_URL = `${WIDGET_ORIGIN}/division/index.html`;
const WIDGET_API_KEY = "058ede2709b4821bc076351701926af7";

const parcelDimensionsMap = {
  A: { length: 64, width: 38, height: 8, actualWeight: 4.86 },
  B: { length: 64, width: 38, height: 19, actualWeight: 10 },
  C: { length: 64, width: 38, height: 41, actualWeight: 24.9 },
};

const getSafeCityLabel = (city) =>
  city?.MainDescription || city?.Present || city?.city || "";

const getSafeRegionLabel = (city) =>
  city?.Area ||
  city?.AreaDescription ||
  city?.Region ||
  city?.RegionDescription ||
  "";

const NovaPoshtaComponent = ({ setFieldValue }) => {
  const deliveryAddress = useSelector(selectDeliveryAddress);
  const parcel = useSelector(selectParcel);

  const [form, setForm] = useState({
    city: deliveryAddress.city || "",
    cityRef: deliveryAddress.cityRef || "",
    warehouse: deliveryAddress.warehouse || "",
    warehouseRef: deliveryAddress.warehouseRef || "",
    region: deliveryAddress.region || "",
  });
  const [citiesList, setCitiesList] = useState([]);
  const [cityError, setCityError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isWidgetOpen, setIsWidgetOpen] = useState(false);
  const [coords, setCoords] = useState({ latitude: "", longitude: "" });

  const iframeRef = useRef(null);
  const selectedCityRef = useRef(null);

  const parcelDimensions = useMemo(() => {
    return (
      parcelDimensionsMap[parcel?.size] || {
        length: 64,
        width: 38,
        height: 8,
        actualWeight: 4.86,
      }
    );
  }, [parcel?.size]);

  useEffect(() => {
    setFieldValue(form);
  }, [form, setFieldValue]);

  useEffect(() => {
    if (!navigator.geolocation) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCoords({
          latitude: String(position.coords.latitude),
          longitude: String(position.coords.longitude),
        });
      },
      (error) => {
        console.warn("[NP Widget] Геолокація недоступна:", error);
      }
    );
  }, []);

  useEffect(() => {
    const handleWidgetMessage = (event) => {
      if (event.origin !== WIDGET_ORIGIN) {
        return;
      }

      console.log("[NP Widget] raw event.data:", event.data);

      if (event.data === "closeFrame") {
        setIsWidgetOpen(false);
        return;
      }

      if (!event.data || typeof event.data !== "object") {
        return;
      }

      const selectedPoint = {
        warehouse:
          event.data.shortName ||
          event.data.name ||
          event.data.label ||
          "Обране відділення Нової пошти",
        warehouseRef:
          event.data.externalId ||
          event.data.id ||
          event.data.ref ||
          event.data.Ref ||
          event.data.departmentId ||
          "",
        warehouseType:
          event.data.type ||
          event.data.category ||
          event.data.addressType ||
          "",
        city:
          event.data.addressParts?.city ||
          selectedCityRef.current?.city ||
          form.city,
        street: event.data.addressParts?.street || "",
        building: event.data.addressParts?.building || "",
      };

      setForm((prev) => {
        const next = {
          ...prev,
          city: selectedPoint.city || prev.city,
          warehouse: selectedPoint.warehouse,
          warehouseRef: selectedPoint.warehouseRef,
        };

        console.log("[NP Widget] normalized selection:", next);
        return next;
      });

      setIsWidgetOpen(false);
    };

    window.addEventListener("message", handleWidgetMessage);

    return () => {
      window.removeEventListener("message", handleWidgetMessage);
    };
  }, [form.city]);

  const handleFormChangeCity = async (event) => {
    const value = event.target.value;
    const regex = /^[А-Яа-яЇїІіЄєҐґ\s'-]+$/;

    setForm((prev) => ({
      ...prev,
      city: value,
      cityRef: "",
      warehouse: "",
      warehouseRef: "",
      region: "",
    }));

    if (!regex.test(value) && value !== "") {
      setCityError("Дозволені тільки кирилиця");
      setCitiesList([]);
      return;
    }

    setCityError("");

    if (value === "") {
      setCitiesList([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetchCitiesList(value);
      setCitiesList(response.data[0]?.Addresses || []);
    } catch (error) {
      console.error("Помилка при завантаженні міст", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCityClick = (cityItem) => {
    const selectedCity = {
      city: getSafeCityLabel(cityItem),
      cityRef: cityItem?.DeliveryCity || "",
      region: getSafeRegionLabel(cityItem),
    };

    selectedCityRef.current = selectedCity;

    setForm((prev) => ({
      ...prev,
      city: selectedCity.city,
      cityRef: selectedCity.cityRef,
      region: selectedCity.region,
      warehouse: "",
      warehouseRef: "",
    }));

    setCitiesList([]);

    console.log("[NP Widget] selected city for widget:", {
      city: selectedCity.city,
      cityRef: selectedCity.cityRef,
      region: selectedCity.region,
      parcelSize: parcel?.size,
      parcelDimensions,
      insuranceCost: parcel?.valuation || 0,
    });
  };

  const widgetQueryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("apiKey", WIDGET_API_KEY);
    params.set("settlement", form.city || "");
    params.set("region", form.region || "");
    params.set("insuranceCost", String(parcel?.valuation || 0));
    params.set("width", String(parcelDimensions.width));
    params.set("length", String(parcelDimensions.length));
    params.set("height", String(parcelDimensions.height));
    params.set("actualWeight", String(parcelDimensions.actualWeight));

    if (coords.latitude) {
      params.set("latitude", coords.latitude);
    }
    if (coords.longitude) {
      params.set("longitude", coords.longitude);
    }

    return params.toString();
  }, [coords.latitude, coords.longitude, form.city, form.region, parcel?.valuation, parcelDimensions]);

  const openWidget = () => {
    if (!form.city || !form.cityRef) {
      setCityError("Спочатку виберіть місто з випадаючого списку");
      return;
    }

    console.log("[NP Widget] opening with params:", {
      settlement: form.city,
      cityRef: form.cityRef,
      region: form.region,
      selectedWarehouseRef: form.warehouseRef || null,
      parcelSize: parcel?.size,
      parcelDimensions,
      insuranceCost: parcel?.valuation || 0,
      coords,
    });

    setIsWidgetOpen(true);
  };

  const closeWidget = () => {
    setIsWidgetOpen(false);
  };

  const handleWidgetLoad = () => {
    const frameWindow = iframeRef.current?.contentWindow;

    if (!frameWindow) {
      return;
    }

    const payload = {
      placeName: form.city,
      id: form.warehouseRef || null,
      latitude: coords.latitude,
      longitude: coords.longitude,
      domain: window.location.hostname,
      apiKey: WIDGET_API_KEY,
      settlement: form.city,
      region: form.region,
      insuranceCost: parcel?.valuation || 0,
      width: parcelDimensions.width,
      length: parcelDimensions.length,
      height: parcelDimensions.height,
      actualWeight: parcelDimensions.actualWeight,
    };

    console.log("[NP Widget] postMessage payload:", payload);
    frameWindow.postMessage(payload, "*");
  };

  const handleClearInput = () => {
    selectedCityRef.current = null;
    setCitiesList([]);
    setCityError("");
    setIsWidgetOpen(false);
    setForm({
      city: "",
      cityRef: "",
      warehouse: "",
      warehouseRef: "",
      region: "",
    });
  };

  return (
    <div className={styles.container}>
      <label className={styles.label}>
        Назва міста
        <input
          type="text"
          name="city"
          value={form.city}
          onChange={handleFormChangeCity}
          className={styles.input}
          required
        />
      </label>

      {cityError && <p className={styles.error}>{cityError}</p>}

      {citiesList.length > 0 && (
        <ul className={styles.list}>
          {citiesList.map((cityItem) => (
            <li
              key={cityItem.DeliveryCity || cityItem.Ref}
              className={styles.listItem}
              onClick={() => handleCityClick(cityItem)}
            >
              {cityItem.Present}
            </li>
          ))}
        </ul>
      )}

      <div className={styles.widgetSection}>
        <p className={styles.widgetTitle}>Відділення або поштомат</p>

        <button
          type="button"
          onClick={openWidget}
          className={styles.widgetButton}
          disabled={!form.cityRef}
        >
          <span className={styles.widgetTextWrap}>
            <span className={styles.widgetText}>
              {form.warehouse || "Обрати відділення або поштомат"}
            </span>
            <span className={styles.widgetDescription}>
              {form.warehouse
                ? "Натисніть, щоб змінити вибрану точку"
                : "Відкриється віджет Нової пошти для вибору точки"}
            </span>
          </span>

          <span className={styles.widgetArrow} aria-hidden="true">
            ›
          </span>
        </button>

        {/*
          Legacy fallback:
          старий пошук/фільтрація відділень залишений тут як швидкий шлях rollback.
          За потреби можемо повернути `fetchBranchesList`, `warehouse` input
          і список `filteredWarehousesList` буквально одним відкатом.
        */}
      </div>

      <div className={styles.loader}>
        <BarLoader color="#007bff" loading={isLoading} width="100%" />
      </div>

      <button
        type="button"
        onClick={handleClearInput}
        className={styles.buttonClear}
      >
        <svg className={styles.svgRemove} width="17" height="14">
          <use className={styles.iconRemove} href={`${icons}#icon-remove`}></use>
        </svg>
        Очистити
      </button>

      {isWidgetOpen && (
        <div className={styles.modalOverlay} onClick={closeWidget}>
          <div
            className={styles.modal}
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Вибрати відділення</h3>
              <button
                type="button"
                onClick={closeWidget}
                className={styles.modalClose}
              >
                ×
              </button>
            </div>
            <iframe
              ref={iframeRef}
              title="Nova Poshta Widget"
              src={`${WIDGET_URL}?${widgetQueryString}`}
              className={styles.modalIframe}
              allow="geolocation"
              onLoad={handleWidgetLoad}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default NovaPoshtaComponent;
